/**
 * Adventure Quest co-op online (2–4 seats): room API, socket and drawing helpers.
 * The gateway (general_services/aq_coop_rooms.py) judges moves, combat, loot and
 * the end; this client only sends its own character's move requests.
 */
import {
  RoomSocket, beaconLeaveRoom, roomInviteLink, roomParamFromSearch, roomsApi, seatStore,
} from './onlineRoomsNet';

export const AQ_GAME = 'aq';
export const AQ_ROOM_PARAM = 'aq_room';
/** While an arrow is held, re-send the move this often (server moves ≤ 5 cells/s). */
export const AQ_MOVE_REPEAT_MS = 120;
export const SEAT_COLOR = {
  player_1: '#4fc3f7', player_2: '#ff8a65', player_3: '#aed581', player_4: '#ba68c8',
};
export const SEAT_NAME = {
  player_1: 'Blue', player_2: 'Orange', player_3: 'Green', player_4: 'Purple',
};

export const aqRooms = roomsApi(AQ_GAME);
const store = seatStore(AQ_GAME);
export const saveAqSeat = (g) => store.save(g);
export const loadAqSeat = () => store.load();
export const clearAqSeat = () => store.clear();
export const beaconLeaveAq = (roomId, token) => beaconLeaveRoom(AQ_GAME, roomId, token);
export const aqRoomIdFromSearch = (search) => roomParamFromSearch(search, AQ_ROOM_PARAM);
export const aqInviteLink = (roomId, loc) => roomInviteLink(roomId, AQ_ROOM_PARAM, loc);

export class AqRoomSocket extends RoomSocket {
  constructor(grant, opts = {}) {
    super(grant, { ...opts, game: AQ_GAME });
  }

  setReady(ready = true) { return this.send({ type: 'ready', ready: Boolean(ready) }); }

  startMatch() { return this.send({ type: 'start' }); }
}

const KEY_MOVES = {
  ArrowUp: 'up', w: 'up', W: 'up',
  ArrowDown: 'down', s: 'down', S: 'down',
  ArrowLeft: 'left', a: 'left', A: 'left',
  ArrowRight: 'right', d: 'right', D: 'right',
};
export function aqMoveForKey(key) {
  return KEY_MOVES[key] || null;
}

/**
 * Who may press Start, and why not yet (mirrors the server check; the server decides).
 * Returns { canStart, reason }.
 */
export function startStatus(seats, mySeat, hostSeat = 'player_1', minSeats = 2) {
  const ids = Object.keys(seats || {});
  if (mySeat !== hostSeat) return { canStart: false, reason: 'Waiting for the host to start.' };
  if (ids.length < minSeats) return { canStart: false, reason: `Need at least ${minSeats} players.` };
  const notReady = ids.filter((id) => id !== hostSeat && !seats[id].ready);
  if (notReady.length) return { canStart: false, reason: `Waiting for ${notReady.length} player(s) to be ready.` };
  const offline = ids.filter((id) => seats[id].human && !seats[id].connected);
  if (offline.length) return { canStart: false, reason: 'Waiting for everyone to connect.' };
  return { canStart: true, reason: '' };
}

/** Paint one co-op frame (grid from welcome meta, entities from the snapshot). */
export function paintAqCoop(ctx, meta, snap, { cell = 32, mySeat } = {}) {
  if (!ctx || !meta || !meta.grid) return;
  const grid = meta.grid;
  const h = grid.length;
  const w = grid[0] ? grid[0].length : 0;
  ctx.fillStyle = '#0d1117';
  ctx.fillRect(0, 0, w * cell, h * cell);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      ctx.fillStyle = grid[y][x] === 1 ? '#3d2c1e' : '#1a2332';
      ctx.fillRect(x * cell, y * cell, cell - 1, cell - 1);
    }
  }
  const ex = meta.exit || { x: w - 2, y: h - 2 };
  ctx.fillStyle = '#2ecc71';
  ctx.fillRect(ex.x * cell, ex.y * cell, cell - 1, cell - 1);
  if (!snap) return;
  (snap.treasures || []).forEach((t) => {
    ctx.fillStyle = t.type === 'gem' ? '#9b59b6' : '#f1c40f';
    ctx.beginPath();
    ctx.arc(t.x * cell + cell / 2, t.y * cell + cell / 2, 8, 0, Math.PI * 2);
    ctx.fill();
  });
  (snap.enemies || []).forEach((e) => {
    ctx.fillStyle = e.type === 'orc' ? '#c0392b' : '#e67e22';
    ctx.fillRect(e.x * cell + 4, e.y * cell + 4, cell - 8, cell - 8);
  });
  const chars = snap.characters || {};
  Object.keys(chars).forEach((sid, i) => {
    const c = chars[sid];
    if (c.retired) return;
    const off = ((i % 2) - 0.5) * 6; // characters sharing the exit stay visible
    ctx.globalAlpha = c.alive ? 1 : 0.35;
    ctx.fillStyle = SEAT_COLOR[sid] || '#fff';
    ctx.beginPath();
    ctx.arc(c.x * cell + cell / 2 + off, c.y * cell + cell / 2 + off, 10, 0, Math.PI * 2);
    ctx.fill();
    if (sid === mySeat) {
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  });
}
